export default {
  title: "Settings",
  description: "Manage your organization settings, users and workspaces.",
  tabs: {
    organization: "Organization",
    subscription: "Subscription",
    models: "AI Models",
    users: "Users",
    workspaces: "Workspaces",
    general: "General",
    logs: "Logs",
    feedback: "Feedback",
    assistant: "Assistant",
    engine: "Engine",
    queues: "Queues",
    plans: "Plans",
    templates: "Templates",
    dangerZone: "Security",
    facets: "Facets",
    emailService: "Email Service",
    emailTemplates: "Email Templates",
    emailNotifications: "Email Notifications"
  },
  v2: {
    groups: {
      organization: "Organization",
      access: "Access"
    }
  },
  superAdmin: {
    title: "Super Admin"
  },
  assistant: {
    description: "Manage Prodgy Assistant settings"
  },
  queues: {
    description: "Manage assistant queues and scheduling"
  },
  templates: {
    comingSoon: "Coming soon",
    tabs: {
      dashboards: "Dashboards",
      emails: "Emails",
      agents: "Agents",
      integrations: "Integrations"
    }
  },
  sidebar: {
    email: "Email",
    emailService: "Service",
    emailTemplates: "Templates",
    emailNotifications: "Notifications",
    emailSender: "Sender",
  },
  models: {
    title: "AI Models",
    description: "Default AI configuration for the organization.",
    updateSuccess: "AI configuration updated",
    updateSuccessDescription: "The default provider and model have been saved successfully.",
    updateError: "Failed to update AI configuration",
    noDeclaredModels: "This integration declares no model for this use.",
    offCatalog:
      "The saved model is not declared in this integration catalog. Pick one from the list before saving.",
    searchModels: "Search models",
    test: {
      running: "Testing the models...",
      unreachable: "The test could not run. Your configuration was saved.",
      title: "Connection test",
      conversational: "Conversational model",
      embedding: "Embedding model",
      skipped: "Not set",
      ok: "Working on {{route}}",
      failed: "Failed on {{route}}",
      routeFrom: {
        declared: "route declared in the registry",
        default: "default route, the model is not declared in the registry",
        invalid_declaration: "the registry declares an invalid `api` value, so the default route was used",
      },
      reason: {
        route_rejected: "The provider rejected this model on this endpoint. The route declared for it is wrong.",
        invalid_credential: "The integration credential was rejected.",
        model_not_found: "The provider could not find this model. Check the deployment name and the API version.",
        provider_unavailable: "The provider did not answer. This does not point to a registry error — try again.",
        gateway_rejected: "The gateway refused this call before reaching the provider. Repeating will return the same — the registry needs to change.",
        unexpected: "Unexpected failure while calling the provider.",
      },
      askSuperadmin: "Ask a superadmin to fix `metadata.models` in this integration's registry.",
      unavailable: {
        no_default_model: "No default model selected — nothing to test.",
        integration_not_found: "The default model integration was not found.",
        not_llm: "The selected integration has no conversational model configured.",
        creds_failed: "Could not read this integration's credential.",
        gateway_not_configured: "The LLM gateway is not configured in this environment; the test did not run.",
      },
    },
  },
  organization: {
    title: "Organization Settings",
    description: "Manage your organization's profile and settings.",
    noProfile: "Information",
    noProfileDescription: "Your profile is not associated with an organization.",
    loadError: "Error loading organization data:",
    unknownError: "Unknown error",
    name: "Organization Name",
    namePlaceholder: "Enter organization name",
    nameRequired: "Organization name is required",
    website: "Website",
    websitePlaceholder: "Enter organization website",
    websiteInvalid: "Enter a valid URL (e.g. https://example.com)",
    info: "Description",
    infoPlaceholder: "Enter organization description",
    defaultModel: "AI Provider",
    conversationalProvider: "Conversational Provider",
    embeddingProvider: "Embedding Provider",
    defaultModelName: "Conversational Model",
    defaultModelNamePlaceholder: "e.g. gpt-4o, claude-sonnet-4-6",
    defaultModelNameHelp: "Used by Playground chats and agent conversations.",
    defaultEmbeddingModel: "Embedding Model",
    defaultEmbeddingModelPlaceholder: "e.g. text-embedding-ada-002, text-embedding-3-small",
    defaultEmbeddingModelHelp: "Used by the Knowledge Base to embed documents and search queries.",
    selectModel: "Select a provider",
    noModel: "No provider",
    noModelsAvailable: "No providers available",
    unknownModel: "Unknown model",
    logo: "Logo",
    logoUpdateSuccess: "Logo updated successfully",
    logoUpdateSuccessDescription: "Your organization logo has been updated.",
    logoUpdateError: "Failed to update logo",
    logoUpdateErrorDescription: "There was an error updating your organization logo.",
    logoUpload: {
      title: "Organization Logo",
      description: "Drag and drop an image or click to select",
      uploadButton: "Upload",
      removeButton: "Remove",
      invalidType: "Invalid file type. Use PNG, JPG or JPEG",
      invalidSize: "Maximum file size: 5MB",
    },
    updateSuccess: "Organization updated successfully",
    updateSuccessDescription: "Your organization settings have been saved.",
    updateError: "Error updating organization",
    updateErrorDescription: "An error occurred while updating your organization settings.",
    saveSuccess: "Organization updated successfully",
    saveError: "Error saving organization",
    dangerZone: {
      title: "Danger Zone",
      description: "Irreversible actions that permanently affect your organization.",
      deleteTitle: "Delete Organization",
      deleteDescription: "Once deleted, there is no going back. Please be certain.",
      deleteButton: "Delete Organization",
      confirmTitle: "Confirm Organization Deletion",
      confirmDescription: "This action is permanent and cannot be undone. All data will be lost.",
      confirmLabel: "Type the organization name to confirm",
      confirmHint: "Type",
      confirmButton: "Delete Permanently",
      deleting: "Deleting...",
      deletingTitle: "Deleting Organization",
      deletingDescription: "Deleting {{name}} and all its data. Please wait...",
      deletingStep: "Processing deletion",
      deletingMessage: "Removing all organization data...",
      deletedMessage: "Organization deleted successfully",
      deleteSuccess: "Organization deleted successfully",
      deleteError: "Error deleting organization",
      deleteErrorDescription: "An error occurred while deleting the organization.",
      deleteErrorInvalid: "Invalid organization",
      deleteErrorNameMismatch: "Organization name does not match",
      deleteErrorWorkspaces: "Cannot delete organization with workspaces",
      deleteErrorWorkspacesDescription: "Remove all workspaces before deleting the organization."
    }
  },
  users: {
    title: "User Management",
    description: "Manage users in your organization.",
    inviteTitle: "Invite User",
    inviteDescription: "Invite users to access the AI agents of your workspaces.",
    organizationUsersTitle: "Organization Users",
    activeUsersTab: "Active Users",
    pendingInvitesTitle: "Pending Invites",
    pendingInvitesTab: "Pending Invites",
    awaitingAccess: "Awaiting access",
    inviteCancelSuccess: "Invite cancelled",
    inviteCancelSuccessDescription: "The invite was successfully cancelled.",
    inviteCancelError: "Error cancelling invite",
    inviteCancelErrorDescription: "An error occurred while cancelling the invite.",
    noOrganizationUsers: "No users found in the organization",
    noInvites: "No pending invites",
    fields: {
      name: "Name",
      email: "Email",
      role: "Role",
      status: "Status",
      workspaces: "Workspaces"
    },
    roles: {
      admin: "Admin",
      user: "User",
      superadmin: "Super Admin"
    },
    selectRole: "Select a role",
    selectWorkspaces: "Select workspaces",
    sendInvite: "Send Invite",
    inviteButton: "Invite Users",
    noName: "No Name",
    inviteSuccess: "Invitation sent successfully",
    inviteSuccessDescription: "The user will receive an email with instructions.",
    inviteError: "Failed to send invitation",
    inviteErrorDescription: "There was an error sending the invitation.",
    duplicateInviteError: "This user has already been invited.",
    userInAnotherOrgError: "This user is already linked to another organization and cannot be invited.",
    usersTable: {
      deleteConfirmTitle: "Delete User",
      deleteConfirmDescription: "Are you sure you want to remove {email} from the organization?",
      deletePendingConfirmDescription: "Are you sure you want to cancel this invitation?",
      inviteDeleteSuccess: "User removed successfully",
      inviteDeleteSuccessDescription: "The user has been removed from your organization.",
      inviteDeleteError: "Failed to remove user",
      inviteDeleteErrorDescription: "There was an error removing the user."
    },
    search: {
      placeholder: "Search by name or email",
      tooltip: "Search users",
      clear: "Clear search",
      noResults: "No user found"
    },
    deleteConfirmTitle: "Delete User",
    deleteConfirmDescription: "Are you sure you want to remove {email} from the organization?",
    deletePendingConfirmDescription: "Are you sure you want to cancel this invitation?",
    deleteSuccess: "User removed successfully",
    deleteSuccessDescription: "The user has been removed from your organization.",
    deleteError: "Failed to remove user",
    deleteErrorDescription: "There was an error removing the user.",
    noWorkspaces: "No workspaces linked",
    superadminAllWorkspaces: "Super Admins have access to all workspaces",
    superadminSectionTitle: "Super Admins",
    otherWorkspaces: "Other workspaces",
    workspacesList: "Workspaces list",
    tabsWorkspaces: "Workspaces",
    tabsRole: "Role",
    editUserWorkspaces: "Edit user workspaces",
    editUserWorkspacesDescription: "Select the workspaces that {name} will have access to",
    editUserWorkspacesHelp: "Check the workspaces you want to link to this user.",
    editUserRoleTitle: "Set user role",
    editUserRoleDescription: "Select the role this user will have in the organization.",
    roleAdmin: "Administrator",
    roleUser: "User",
    editUserWorkspacesButton: "Edit linked workspaces",
    editUserSettings: "Edit user settings",
    noWorkspacesInOrganization: "There are no workspaces in the organization",
    updateWorkspacesSuccess: "Saved successfully!",
    updateWorkspacesSuccessDescription: "The user settings have been saved.",
    updateWorkspacesError: "Error updating workspaces",
    updateWorkspacesErrorDescription: "An error occurred while updating the user's workspaces.",
    deleteUserTitle: "Delete user",
    deleteUserDescription: "Are you sure you want to delete {{name}}? This action cannot be undone.",
    deleteUserButton: "Delete user",
    deleteUserSuccess: "User deactivated",
    deleteUserSuccessDescription: "The user has been successfully deactivated from the system.",
    deleteUserError: "Error deactivating user",
    deleteUserErrorDescription: "An error occurred while trying to deactivate the user. Please try again later.",
    cannotDeleteSelf: "You cannot delete your own user",
    removeFromOrgTitle: "Remove from organization",
    removeFromOrgDescription: "{{name}} loses access to this organization and to all of its workspaces.",
    removeFromOrgButton: "Remove from organization",
    removeFromOrgSuccess: "User removed",
    removeFromOrgSuccessDescription: "This person is no longer part of this organization.",
    removeFromOrgError: "Could not remove",
    removeFromOrgErrorDescription: "We could not remove this person from the organization.",
    unlinkUserTitle: "Remove from workspace",
    unlinkUserDescription: "Are you sure you want to remove {{name}} from this workspace? The user will remain active in the organization.",
    unlinkUserButton: "Remove from workspace",
    unlinkUserSuccess: "User removed from workspace",
    unlinkUserSuccessDescription: "The user was successfully unlinked from the workspace.",
    unlinkUserError: "Error removing user from workspace",
    unlinkUserErrorDescription: "An error occurred while trying to remove the user from the workspace. Please try again.",
    deactivateSuccess: "User successfully deactivated",
    deactivateSuccessDescription: "The user has been deactivated from the system",
    deactivateError: "Error deactivating user",
    deactivateErrorDescription: "An error occurred while deactivating the user",
    loadingUserData: "Loading user data...",
    cancelInvite: "Cancel invite"
  },
  logs: {
    title: "Logs",
    description: "View system logs in real-time",
    selectFile: "Select log file",
    selectFileToView: "Select a log file to view",
    noFilesFound: "No log files found",
    noLogEntries: "No log entries to display",
    refreshFileList: "Refresh file list",
    startStreaming: "Start real-time monitoring",
    stopStreaming: "Stop real-time monitoring",
    start: "Start",
    stop: "Stop",
    clearLogs: "Clear logs",
    lineCount: "{{count}} lines",
    streamingActive: "Real-time monitoring active",
    streamingStopped: "Real-time monitoring inactive",
    liveStreaming: "Receiving logs in real-time",
    terminalTitle: "System Log Terminal",
    fullscreenTitle: "Fullscreen Terminal"
  },
  feedback: {
    title: "Feedback",
    description: "Configure when the user feedback form appears and the GA4 settings.",
    enabled: "Enable Feedback",
    enabledDescription: "When disabled, the feedback popup is hidden for everyone — including users who never responded.",
    sessionTimeout: "Session timeout (minutes)",
    sessionTimeoutDescription: "Time after login before the feedback popup is shown. 0 disables this trigger.",
    sessionCountThreshold: "Session count threshold (logins)",
    sessionCountThresholdDescription: "Show the form once the user reaches this many logins. Combined with the timeout via OR — whichever triggers first wins. 0 disables this trigger.",
    reminderTimeout: "Reminder timeout (minutes)",
    ga4Enabled: "Enable GA4 events",
    ga4EnabledDescription: "When enabled, the frontend will send GA4 events for impression, submission and dismissal.",
    ga4EventName: "GA4 event name",
    updateSuccess: "Feedback settings updated",
    updateError: "Failed to update feedback settings",
    missingConfig: "Run the feedback_form settings migration before editing here.",
    resetTitle: "Reset user feedback",
    resetDescription: "Clear all submitted answers and reopen the feedback form for every user. Historical events are preserved.",
    resetButton: "Reset Feedback",
    resetting: "Resetting...",
    resetSuccess: "Feedback reset successfully",
    resetCount: "{{count}} profiles were reset.",
    resetError: "Failed to reset user feedback"
  },
  emailSender: {
    useTemplate: "Use a template",
    useTemplateDescription: "Load a base template into the editor, or compose the email from scratch.",
    template: "Template",
    selectTemplate: "Select a template…",
    subject: "Subject",
    preview: "Preview",
    placeholderHint: "Only {{userName}} is replaced per recipient at send time. Fill any other {{placeholders}} before sending.",
    testEmailTitle: "Test email",
    testEmailDescription: "Sends BOTH the EN and PT versions to this address so you can review them.",
    sendTest: "Send test",
    testSent: "Test emails dispatched",
    allUsersTitle: "Database users",
    allUsersDescription: "Sends to every registered user, picking the EN or PT version based on their profile language. Runs in the background.",
    sendAll: "Send to all users",
    bulkStarted: "Bulk send started",
    confirmTitle: "Send to ALL registered users?",
    confirmDescription: "This will email every registered user in the database. This action cannot be undone. The send runs in the background.",
    confirmSend: "Yes, send to all"
  },
  emailTemplates: {
    title: "Email Templates",
    description: "Manage email templates for your organization.",
    tabs: {
      base: "Base Templates",
      organization: "Organization"
    },
    noTemplates: "No templates found",
    searchPlaceholder: "Search templates...",
    columns: {
      name: "Template",
      slug: "Slug",
      subject: "Subject",
      language: "Language",
      status: "Status",
      actions: "Actions"
    },
    customized: "Customized",
    default: "Default",
    usingSystemDefault: "Using system default",
    edit: "Edit",
    preview: "Preview",
    remove: "Remove",
    removeCustomization: "Remove customization",
    createCustom: "Customize",
    editor: {
      subject: "Subject",
      content: "Content",
      variables: "Available variables",
      save: "Save",
      saving: "Saving...",
      reset: "Restore Default",
      removeCustomization: "Remove Customization",
      back: "Back",
      manageVariables: "Manage variables",
      variableKey: "Key",
      variableDescription: "Description",
      variableExample: "Example",
      addVariable: "Add variable",
      noVariables: "No variables defined",
      keyRequired: "Key is required"
    },
    saveSuccess: "Template saved successfully",
    saveError: "Failed to save template",
    deleteSuccess: "Custom template removed",
    deleteError: "Failed to remove template",
    resetSuccess: "Template restored to default",
    resetError: "Failed to restore template"
  },
  emailService: {
    title: "Email Service Configuration",
    description: "Configure the email service for sending invitations, password resets and notifications.",
    sendingMethod: "Sending Method",
    emailProtocol: "Email Protocol",
    generalSettings: "General Settings",
    fromEmail: "From Email",
    organizationName: "Organization Name",
    testConfiguration: "Test Configuration",
    testEmail: "Test Email",
    testEmailPlaceholder: "your.email@example.com",
    testEmailHint: "Enter an email to test the configuration before saving.",
    testConnection: "Test Connection",
    saveConfiguration: "Save Configuration",
    reset: "Reset",
    loading: "Loading configuration...",
    loadError: "Error loading email configuration. Check if migrations have been executed.",
    saveSuccess: "Configuration saved!",
    saveError: "Error saving",
    testRequired: "Test email is required",
    testSuccess: "Test successful!",
    testFailed: "Test failed",
    smtpSection: "SMTP",
    smtpHost: "SMTP Host",
    smtpPort: "Port",
    smtpUser: "SMTP User",
    smtpPassword: "SMTP Password",
  },
  emailNotifications: {
    title: "Email Notifications",
    description: "Configure which emails Prodgy sends automatically.",
    missingConfig: "Run the email_notifications seed before editing here.",
    meetingConfirmation: "Meeting confirmation",
    meetingConfirmationDescription: "Sent to all attendees when the Assistant accepts a meeting invite.",
    meetingCancellation: "Meeting cancellation",
    meetingCancellationDescription: "Sent when a meeting with the Assistant is cancelled.",
    meetingError: "Assistant errors",
    meetingErrorDescription: "Sent to the organizer when the Assistant cannot join (unregistered user, no workspace, etc).",
    groupAssistant: "Assistant",
    groupApiTokens: "API Tokens",
    groupUserAccount: "User / Account",
    apiTokenCreated: "Token created",
    apiTokenCreatedDescription: "Sent to the user when a new API token is created.",
    apiTokenExpiring: "Token expiring soon",
    apiTokenExpiringDescription: "Sent when an API token is about to expire.",
    apiTokenExpired: "Token expired",
    apiTokenExpiredDescription: "Sent when an API token has expired.",
    apiTokenRevoked: "Token revoked",
    apiTokenRevokedDescription: "Sent when an API token is revoked.",
    invite: "User invitation",
    inviteDescription: "Sent to users when they are invited to the organization.",
    alreadyRegistered: "Email already registered",
    alreadyRegisteredDescription: "Sent when a signup attempt is made with an already-registered email.",
    meetingScriberWelcome: "Meeting Scriber welcome",
    meetingScriberWelcomeDescription: "Sent to users when they are onboarded to Meeting Scriber.",
    saveSuccess: "Notification settings saved",
    saveError: "Failed to save notification settings"
  },
  plansAdmin: {
    tabs: {
      plans: "Plans",
      waitlists: "Waitlists"
    },
    list: {
      newPlan: "New Plan",
      name: "Name",
      slug: "Slug",
      price: "Price",
      billing: "Billing",
      permissionList: "Permission List",
      active: "Active",
      actions: "Actions",
      empty: "No plans yet. Click \"New Plan\" to get started."
    },
    delete: {
      title: "Delete plan?",
      description: "This will permanently delete {{name}}. This action cannot be undone.",
      cancel: "Cancel",
      confirm: "Delete",
      deleting: "Deleting…"
    },
    editor: {
      newTitle: "New Plan",
      editTitle: "Edit Plan — {{name}}",
      tabs: {
        general: "General",
        features: "Features & Metadata",
        limits: "Limits & Credits",
        access: "Access Control"
      },
      name: "Name",
      slug: "Slug",
      slugPlaceholder: "e.g. trial",
      sortOrder: "Sort Order",
      active: "Active",
      description: "Description",
      billingCycle: "Billing Cycle",
      billingSelectPlaceholder: "Select…",
      billingMonthly: "Monthly",
      billingSemiAnnual: "Semi-annual",
      billingAnnual: "Annual",
      billingLifetime: "Lifetime",
      trialDays: "Trial Days",
      priceUsd: "Price (USD)",
      priceBrl: "Price (BRL)",
      invoiceEnabled: "Invoice enabled",
      features: "Features",
      restrictions: "Restrictions",
      badge: "Badge",
      ctaText: "CTA Text",
      color: "Color",
      colorPlaceholder: "e.g. blue",
      popular: "Popular",
      highlight: "Highlight",
      add: "Add",
      english: "English",
      portuguese: "Português",
      creditsLimit: "Credits Limit (null = unlimited)",
      creditsLimitPlaceholder: "Leave empty for unlimited",
      creditsLabel: "Credits Label",
      creditUnitCosts: "Credit Unit Costs",
      fixedLimits: "Fixed Limits",
      keyPlaceholder: "Key",
      costPlaceholder: "Cost",
      labelEnPlaceholder: "Label EN",
      maxPlaceholder: "Max (null=∞)",
      permissionList: "Permission List (Waitlist)",
      permissionListNone: "None",
      permissionListHint: "When set, access to this plan is gated by membership in the selected waitlist.",
      integrationsKey: "Integrations List Key",
      integrationsKeyPlaceholder: "e.g. trial_integrations",
      integrationsKeyHint: "Name of the allowed integrations list for this plan.",
      cancel: "Cancel",
      saving: "Saving…",
      saveChanges: "Save Changes",
      createPlan: "Create Plan",
      saveError: "Failed to save plan.",
      errors: {
        nameRequired: "Enter the plan name.",
        slugRequired: "Enter the slug.",
        slugInvalid: "Use only lowercase letters, numbers and hyphens.",
        summary: "Review the highlighted fields."
      }
    }
  },
  waitlists: {
    title: "Waitlists",
    description: "Manage access control lists linked to plans.",
    newWaitlist: "New Waitlist",
    empty: "No waitlists yet. Click \"New Waitlist\" to create one.",
    contactsCount: "{{count}} contacts",
    newDialog: {
      title: "New Waitlist",
      keyLabel: "Key (settings type)",
      keyPlaceholder: "e.g. waitlist_beta",
      keyHint: "Unique identifier used in plan's permission_list field.",
      label: "Label",
      english: "English",
      portuguese: "Português",
      createError: "Failed to create waitlist.",
      cancel: "Cancel",
      creating: "Creating…",
      create: "Create Waitlist"
    },
    contacts: {
      total: "Total",
      searchPlaceholder: "Search email…",
      addContact: "Add Contact",
      email: "Email",
      status: "Status",
      noMatch: "No contacts match your search.",
      empty: "No contacts yet.",
      removeTitle: "Remove contact?",
      removeDescription: "This will remove {{email}} from the waitlist permanently.",
      cancel: "Cancel",
      removing: "Removing…",
      remove: "Remove"
    },
    addDialog: {
      title: "Add Contact",
      email: "Email",
      emailPlaceholder: "user@example.com",
      status: "Status",
      invalidEmail: "Enter a valid email address.",
      addError: "Failed to add contact.",
      cancel: "Cancel",
      adding: "Adding…",
      add: "Add Contact"
    }
  },
  dashboardLayouts: {
    title: "Default layouts",
    intro: "Default layouts per scope. They appear for users without their own layout and for organizations/workspaces without a specific one. To set one, open the dashboards screen for the matching scope and click",
    introBold: "Save as Default → Superadmin / All Organizations / All Workspaces",
    loading: "Loading…",
    noLayout: "No layout set",
    tabs_one: "{{count}} tab",
    tabs_other: "{{count}} tabs",
    slots_one: "{{count}} slot",
    slots_other: "{{count}} slots",
    clear: "Clear",
    clearing: "Clearing…",
    clearTitle: "Clear default layout?",
    clearDescription: "Users without their own layout (and orgs/products without a specific one) will see an empty dashboard in the {{scope}} scope.",
    cancel: "Cancel",
    cleared: "Default layout cleared",
    clearFailed: "Failed: {{message}}",
    scopes: {
      superadminLabel: "Superadmin",
      superadminDescription: "Default layout superadmins see when opening the dashboards screen (platform scope).",
      organizationLabel: "Organization",
      organizationDescription: "Default layout users see on organization dashboards when there is no override.",
      productLabel: "Workspace",
      productDescription: "Default layout for workspace dashboards when there is no override."
    }
  },
  dashboardTemplateEditor: {
    editTitle: "Edit dashboard template",
    newTitle: "New dashboard template",
    tabBasic: "Basic",
    tabDatasources: "Datasources",
    tabAdvanced: "Advanced",
    tabPreview: "Preview",
    nameEn: "Name (English)",
    namePt: "Name (Portuguese)",
    namePlaceholderEn: "Platform Health",
    namePlaceholderPt: "Platform Health",
    descEn: "Description (English)",
    descPt: "Description (Portuguese)",
    category: "Category",
    icon: "Icon",
    type: "Type",
    scope: "Scope",
    active: "Active (distribute to new organizations/products)",
    datasourcesHint1: "List of datasources as a JSON array. Each item:",
    datasourcesHint2: "For",
    datasourcesHint3: "use one of the metrics from the catalog below.",
    metricsCatalog: "Internal metrics catalog",
    noMetrics: "No metrics available for this scope.",
    previewHint: "Runs the datasources with real data (superadmin scope for the superadmin scope, or your organization for the others). Nothing is persisted.",
    runningPreview: "Running…",
    runPreview: "Run preview",
    cancel: "Cancel",
    saving: "Saving…",
    save: "Save",
    create: "Create",
    templateUpdated: "Template updated",
    templateCreated: "Template created",
    nameRequired: "Name (en and pt) is required.",
    unknownError: "Unknown error"
  },
  workspaces: {
    title: "Workspaces",
    description: "Manage your agent workspaces.",
    addWorkspace: "Workspace",
    editWorkspace: "Edit Workspace",
    dialogDescription: "Enter the workspace details below.",
    fields: {
      name: "Name",
      namePlaceholder: "Enter workspace name",
      slug: "Slug",
      slugPlaceholder: "workspace-slug",
      slugHelper: "Identifier used in communicator commands (e.g., /prodgy use my-workspace). Auto-generated from name if empty.",
      description: "Description",
      descriptionPlaceholder: "Enter workspace description",
      website: "Website",
      websitePlaceholder: "https://example.com",
      websiteHelper: "You can enter with or without https:// (e.g., example.com)",
      logo: "Logo",
      userName: "User Name",
      userEmail: "Email",
      access: "Access",
      users: "Users",
      avatar: "Avatar"
    },
    generalTab: "General Information",
    usersTab: "Users",
    noName: "No Name",
    saveSuccess: "Workspace saved successfully",
    saveSuccessDescription: "Your workspace has been saved.",
    saveError: "Failed to save workspace",
    saveErrorDescription: "There was an error saving the workspace.",
    deleteConfirmTitle: "Delete Workspace",
    deleteConfirmDescription: "Are you sure you want to delete this workspace? This action cannot be undone.",
    deleteSuccess: "Workspace deleted successfully",
    deleteSuccessDescription: "The workspace and all its dependencies have been removed.",
    deletePartialSuccess: "Workspace partially deleted",
    deletePartialSuccessDescription: "Some workspace dependencies may not have been completely removed.",
    deleteError: "Failed to delete workspace",
    deleteErrorDescription: "There was an error deleting the workspace.",
    deleteBlockedDescription: "Remove the {{blockers}} from this workspace before deleting it.",
    deletingWorkspace: "Deleting workspace",
    deletingWorkspaceMessage: "Deleting {{workspaceName}} and its dependencies. Please wait...",
    logoUpdateSuccess: "Logo updated successfully",
    logoUpdateSuccessDescription: "The workspace logo has been updated.",
    logoUpdateError: "Failed to update logo",
    users: "users",
    searchUsers: "Search users...",
    noUsersInOrg: "No users found in the organization",
    noUsersFound: "No users found",
    fillRequiredFields: "Please fill in all required fields",
    nameRequired: "Workspace name is required",
    inviteUserCta: "Invite more users to your workspace right now!",
    noOtherUsers: "There are no other users in the organization besides you.",
    addUsersButton: "+ Users",
    alreadyMember: "Already member",
    meLabel: "(Me)",
    pendingInvites: "Sent Invites",
    noWorkspaces: "No workspaces registered",
    noWorkspacesDescription: "Start by creating your first workspace to manage AI agents and resources.",
    errors: {
      nameRequired: "Workspace name is required",
      organizationNotFound: "Organization not found",
      saveFailed: "Failed to save workspace"
    },
    messages: {
      updateSuccess: "Workspace information updated successfully"
    },
    apiTokens: {
      title: "API Tokens",
      description: "Manage workspace-level API tokens for third-party access",
      create: "Create",
      createToken: "Create API Token",
      createDescription: "Create a new token linked to this workspace.",
      successDescription: "Your API token has been created successfully. You can copy it now or access it later from the token list.",
      tokenName: "Token name",
      tokenNamePlaceholder: "e.g. Production Integration",
      setExpiration: "Set expiration",
      expiresAt: "Expires at",
      cancel: "Cancel",
      done: "Done",
      yourApiToken: "Your API Token",
      copyToken: "Copy token",
      copyFullToken: "Copy full token (expires in 30s)",
      copyPrefix: "Copy token prefix (regenerate to get full token)",
      regenerate: "Regenerate",
      regenerateTitle: "Regenerate token - creates a new token and shows it to you",
      regenerateSuccess: "Token regenerated successfully",
      active: "Active",
      inactive: "Inactive",
      created: "Created",
      expires: "Expires",
      usageCount: "Usage count",
      noTokens: "No API tokens",
      noTokensDescription: "Create an API token to allow third-party integrations to access scoped endpoints.",
      createFirstToken: "Create your first token",
      availableApis: "Available APIs",
      apisDescription: "Endpoints accessible with API tokens from this workspace",
      swaggerLink: "For full endpoint details, see the Swagger documentation",
      tokenCopied: "Token copied to clipboard",
      tokenPrefixCopied: "Token prefix copied to clipboard",
      important: "Important: Copy this token now! After closing this dialog, only the token prefix will be shown for security reasons. The full token cannot be retrieved later.",
      error: "Error",
      failedToCreate: "Failed to create API token",
      failedToRevoke: "Failed to revoke token",
      failedToRenew: "Failed to renew token",
      failedToRegenerate: "Failed to regenerate token",
      failedToCopy: "Failed to copy token",
      failedToCopyPrefix: "Failed to copy token prefix",
      tokenRevoked: "Token revoked",
      tokenRenewed: "Token renewed successfully"
    }
  }
} as const;
